import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Paper,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import api from '../services/api';
import IntervalTable from '../components/IntervalTable';
import AddIntervalForm from '../components/AddIntervalForm';

export default function UpdateAccessPage() {
  const { username } = useParams();
  const navigate = useNavigate();

  const [accessAll, setAccessAll] = useState(false);
  const [intervals, setIntervals] = useState([]);
  const [bulkJson, setBulkJson] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line
  }, [username]);

  async function fetchUser() {
    try {
      const response = await api.get(`/admin/users/${username}`);
      const userData = response.data;

      if (userData.access?.all) {
        setAccessAll(true);
        setIntervals([]);
      } else {
        const clientAccess = userData.access?.client_access || {};
        const builtIntervals = [];
        Object.keys(clientAccess).forEach((cid) => {
          clientAccess[cid].forEach((entry) => {
            builtIntervals.push({
              client_id: cid,
              filename: entry.filename,
              start_date: entry.start_date,
              end_date: entry.end_date,
            });
          });
        });
        setIntervals(builtIntervals);
      }
      setStatusMsg('');
    } catch (error) {
      console.error(error);
      setStatusMsg('Failed to load user details');
    }
  }

  const buildAccessObject = () => {
    if (bulkJson.trim()) {
      return JSON.parse(bulkJson);
    } else if (accessAll) {
      return { all: true };
    } else {
      const clientAccess = {};
      intervals.forEach((interval) => {
        const cid = interval.client_id;
        if (!clientAccess[cid]) {
          clientAccess[cid] = [];
        }
        clientAccess[cid].push({
          filename: interval.filename,
          start_date: interval.start_date,
          end_date: interval.end_date,
        });
      });
      return {
        all: false,
        client_access: clientAccess,
      };
    }
  };

  const handleUpdateAccess = async () => {
    try {
      const accessObj = buildAccessObject();
      await api.put(`/admin/users/${username}/access`, accessObj);
      setStatusMsg('User access updated successfully!');
      openSnackbar('All access updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error(error);
      setStatusMsg(`Error updating access: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleAddInterval = async (newInterval) => {
    try {
      await api.post(`/admin/users/${username}/access/interval`, {
        client_id: newInterval.client_id,
        filename: newInterval.filename,
        start_date: newInterval.start_date,
        end_date: newInterval.end_date,
      });
      openSnackbar('New interval added successfully!');
      fetchUser();
    } catch (err) {
      console.error(err);
      openSnackbar(`Failed to add interval: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleUpdateRow = async (intervalData) => {
    try {
      await api.patch(`/admin/users/${username}/access/interval`, {
        client_id: intervalData.client_id,
        filename: intervalData.filename,
        start_date: intervalData.start_date,
        end_date: intervalData.end_date,
      });
      openSnackbar('Interval updated successfully!');
      fetchUser();
    } catch (err) {
      console.error(err);
      openSnackbar(`Failed to update interval: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleBulkAddAccess = async () => {
    try {
      const parsedJson = JSON.parse(bulkJson);
      await api.post(`/admin/users/${username}/access/bulk`, parsedJson);
      openSnackbar('Bulk access intervals added successfully!');
      fetchUser();
    } catch (err) {
      console.error(err);
      openSnackbar(`Bulk add failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleActivateThread = async () => {
    try {
      const resp = await api.post(`/admin/activate-thread/${username}`);
      openSnackbar(
        resp.data.message +
        (resp.data.active_filename ? ` (Active: ${resp.data.active_filename})` : '')
      );
      fetchUser();
    } catch (err) {
      console.error(err);
      openSnackbar(`Failed to activate thread: ${err.response?.data?.detail || err.message}`);
    }
  };

  const openSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const closeSnackbar = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={4} sx={{ p: 4 }}>
          <Grid container spacing={3}>
            {/* Header */}
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom>
                Update Access - {username}
              </Typography>
              <Divider />
            </Grid>

            {/* Access All toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={accessAll}
                    onChange={(e) => setAccessAll(e.target.checked)}
                    color="primary"
                  />
                }
                label="Grant Access to All"
              />
            </Grid>

            {/* Interval Table and Bulk JSON */}
            {!accessAll && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Access Intervals
                  </Typography>
                  <IntervalTable
                    intervals={intervals}
                    setIntervals={setIntervals}
                    onUpdateRow={handleUpdateRow}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Add New Interval
                  </Typography>
                  <AddIntervalForm onIntervalAdded={handleAddInterval} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    OR Paste Bulk JSON:
                  </Typography>
                  <TextField
                    label="Bulk Access JSON"
                    multiline
                    minRows={4}
                    value={bulkJson}
                    onChange={(e) => setBulkJson(e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
              </>
            )}

            {/* Action Buttons */}
            <Grid item xs={12} sx={{ textAlign: 'right' }}>
              <Button variant="contained" color="primary" onClick={handleUpdateAccess}>
                Update Access (Full)
              </Button>

              <Button
                variant="outlined"
                color="primary"
                onClick={handleBulkAddAccess}
                sx={{ ml: 2 }}
              >
                Add Bulk Intervals
              </Button>

              <Button
                variant="contained"
                color="secondary"
                onClick={handleActivateThread}
                sx={{ ml: 2 }}
              >
                Activate Thread
              </Button>
            </Grid>

            {/* Status Message */}
            {statusMsg && (
              <Grid item xs={12}>
                <Typography color="info.main">{statusMsg}</Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Snackbar Notification */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={closeSnackbar} severity="success" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
}
